import { ApolloLink, FetchResult, Observable, Operation } from '@apollo/client';
import { print } from 'graphql';

export interface SSELinkOptions {
	uri: string;
	headers?: () => Record<string, string>;
}

export class SSELink extends ApolloLink {
	private uri: string;
	private headers: () => Record<string, string>;

	constructor(options: SSELinkOptions) {
		super();
		this.uri = options.uri;
		this.headers = options.headers || (() => ({}));
	}

	public request(operation: Operation): Observable<FetchResult> | null {
		return new Observable<FetchResult>((observer) => {
			const controller = new AbortController();

			// GraphQL Yoga expects a POST request with SSE response
			fetch(this.uri, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Accept: 'text/event-stream',
					...this.headers(),
				},
				// Send HttpOnly cookies so authenticated subscriptions work cross-origin.
				credentials: 'include',
				body: JSON.stringify({
					query: print(operation.query),
					variables: operation.variables,
					operationName: operation.operationName,
				}),
				signal: controller.signal,
			})
				.then(async (response) => {
					if (!response.ok) {
						throw new Error(`HTTP error! status: ${response.status}`);
					}

					const reader = response.body?.getReader();
					const decoder = new TextDecoder();

					if (!reader) {
						throw new Error('No response body');
					}

					let buffer = '';
					let isDone = false;

					while (!isDone) {
						const { done, value } = await reader.read();

						if (done) {
							observer.complete();
							isDone = true;
							break;
						}

						buffer += decoder.decode(value, { stream: true });
						const lines = buffer.split('\n');

						// Keep the last incomplete line in the buffer
						buffer = lines.pop() || '';

						for (const line of lines) {
							if (line.startsWith('data: ')) {
								try {
									const data = JSON.parse(line.slice(6));
									observer.next(data);
								} catch (error) {
									console.error('Failed to parse SSE data:', error);
								}
							}
						}
					}
				})
				.catch((error) => {
					if (error.name !== 'AbortError') {
						observer.error(error);
					}
				});

			// Cleanup on unsubscribe
			return () => {
				controller.abort();
			};
		});
	}
}
